import { useTranslations } from 'next-intl'
import { Flight } from '../types/itinerary'
import { capitalizeFirstLetter } from './generalUtils/date'
import { isEmpty } from './generalUtils/isEmpty'

export function deepclone(item) {
    if (!item) {
        return item
    } // null, undefined values check

    var types = [Number, String, Boolean],
        result

    // normalizing primitives if someone did new String('aaa'), or new Number('444');
    types.forEach(function (type) {
        if (item instanceof type) {
            result = type(item)
        }
    })

    if (typeof result == 'undefined') {
        if (Object.prototype.toString.call(item) === '[object Array]') {
            result = []
            item.forEach(function (child, index, array) {
                result[index] = deepclone(child)
            })
        } else if (typeof item == 'object') {
            // testing that this is DOM
            if (item.nodeType && typeof item.cloneNode == 'function') {
                result = item.cloneNode(true)
            } else if (!item.prototype) {
                // check that this is a literal
                if (item instanceof Date) {
                    result = new Date(item)
                } else {
                    // it is an object literal
                    result = {}
                    for (var i in item) {
                        result[i] = deepclone(item[i])
                    }
                }
            } else {
                // depending what you would like here,
                // just keep the reference, or create new object
                if (false && item.constructor) {
                    // would not advice to do that, reason? Read below
                    result = new item.constructor()
                } else {
                    result = item
                }
            }
        } else {
            result = item
        }
    }

    return result
}
export const fixFlightDataConfirmation = (flightData: any) => {
    let tempDataHolder = ''
    let newFlightData = deepclone(flightData)

    newFlightData.segments.forEach((segment: any) => {
        tempDataHolder = segment.arrivalAirport
        segment.arrivalAirport = segment.arrivalAirportIata
        segment.arrivalAirportIata = tempDataHolder
        segment.arrivalAirport = segment.arrivalAirport.split(',')[0]
        tempDataHolder = segment.departAirport
        segment.departAirport = segment.departAirportIata
        segment.departAirportIata = tempDataHolder
        segment.departAirport = segment.departAirport.split(',')[0]
    })
    return newFlightData
}

export const convertPricingDetails = (
    pricingData: any,
    vasDetails: any,
    paymentDetails?: any,
    vatApplicable?: boolean,
    additionalVatFromVas?: number
) => {
    const t = useTranslations('itineraryPage.pricing')

    if (isEmpty(pricingData)) return
    let newPricingData = { pricingElements: [], finalPricing: {} }
    let pricingElements: any = []
    let finalPricing = {}
    const pricingDataKeys = Object.keys(pricingData)
    pricingDataKeys.forEach(item => {
        if (pricingData[item] !== 0) {
            if (item === 'baseFare') {
                pricingElements.push({
                    displayText: t('baseFare'),
                    type: 'BASE_FARE',
                    tooltipMessage: null,
                    amount: pricingData[item],
                    pricingCategory: 'BF',
                    strikeOffDetails: null
                })
            }
            if (item === 'taxes') {
                const vatToDeduct = !vatApplicable ? 0 : pricingData['totalVat'] || 0
                pricingElements.push({
                    displayText: t('tax'),
                    type: 'TAX',
                    tooltipMessage: null,
                    amount: pricingData[item] - vatToDeduct,
                    pricingCategory: 'TAX',
                    strikeOffDetails: null
                })
            }
            if (item === 'convenienceFee') {
                pricingElements.push({
                    displayText: t('convenienceFee'),
                    type: 'FEE',
                    tooltipMessage: null,
                    amount: pricingData[item],
                    pricingCategory: 'FEE',
                    strikeOffDetails: null
                })
            }
            if (item === 'cashback') {
                pricingElements.push({
                    displayText: t('cashback'),
                    type: 'COUPON',
                    tooltipMessage: null,
                    amount: pricingData[item],
                    pricingCategory: 'DIS',
                    strikeOffDetails: null
                })
            }
            if (item === 'discount') {
                pricingElements.push({
                    displayText: t('discounts'),
                    type: 'DISCOUNT',
                    tooltipMessage: null,
                    amount: pricingData[item],
                    pricingCategory: 'DIS',
                    strikeOffDetails: null
                })
            }
            if (item === 'baggageCharges') {
                pricingElements.push({
                    displayText: t('baggageSelection'),
                    type: 'BAGGAGE',
                    tooltipMessage: null,
                    amount: pricingData[item],
                    pricingCategory: 'FEE',
                    strikeOffDetails: null
                })
            }
            if (item === 'oldFare') {
                pricingElements.push({
                    displayText: 'Paid Before',
                    type: 'AMEND',
                    tooltipMessage: null,
                    amount: pricingData[item],
                    pricingCategory: 'DIS',
                    strikeOffDetails: null
                })
            }
            if (item === 'amendCharges') {
                pricingElements.push({
                    displayText: 'Airline Charges',
                    type: 'AMEND',
                    tooltipMessage: null,
                    amount: pricingData[item],
                    pricingCategory: 'FEE',
                    strikeOffDetails: null
                })
            }
            if (item === 'ctAmendCharges') {
                pricingElements.push({
                    displayText: 'CT Charges',
                    type: 'AMEND',
                    tooltipMessage: null,
                    amount: pricingData[item],
                    pricingCategory: 'FEE',
                    strikeOffDetails: null
                })
            }
            if (item === 'amendReverseAmt') {
                pricingElements.push({
                    displayText: 'Insurance Benefit',
                    type: 'AMEND',
                    tooltipMessage: null,
                    amount: pricingData[item],
                    pricingCategory: 'FEE',
                    strikeOffDetails: null
                })
            }
            if (item === 'totalVat' && vatApplicable) {
                // Include additional VAT from vasDetails (similar to itinerary page logic)
                const totalVatAmount = (pricingData[item] || 0) + (additionalVatFromVas || 0)
                if (totalVatAmount > 0) {
                    pricingElements.push({
                        displayText: t('vat'),
                        type: 'AMEND',
                        tooltipMessage: null,
                        amount: totalVatAmount,
                        pricingCategory: 'FEE',
                        strikeOffDetails: null
                    })
                }
            }
            if (item === 'mealCharges') {
                pricingElements.push({
                    displayText: t('mealSelection'),
                    type: 'MEAL',
                    tooltipMessage: null,
                    amount: pricingData[item],
                    pricingCategory: 'FEE',
                    strikeOffDetails: null
                })
            }
            if (item === 'seatCharges') {
                pricingElements.push({
                    displayText: t('seatSelection'),
                    type: 'SEAT',
                    tooltipMessage: null,
                    amount: pricingData[item],
                    pricingCategory: 'FEE',
                    strikeOffDetails: null
                })
            }
            if (item === 'insuranceAmount' && !isEmpty(vasDetails)) {
                vasDetails?.forEach((vasDetail: any) => {
                    const { title: vasTitle = '', amount: vasAmount = 0 } = vasDetail || {}
                    const formattedTitle = ['ClearChoice Max', 'EzCancel', 'ClearChoice Plus'].includes(vasTitle)
                        ? vasTitle
                        : capitalizeFirstLetter(vasTitle || '')
                    pricingElements.push({
                        displayText: formattedTitle,
                        type: 'INSURANCE',
                        tooltipMessage: null,
                        amount: vasAmount,
                        pricingCategory: 'FEE',
                        strikeOffDetails: null
                    })
                })
            }

            if (item === 'totalFare') {
                finalPricing = {
                    displayText: t('totalBookingAmount'),
                    type: 'TOTAL',
                    tooltipMessage: null,
                    amount: pricingData[item],
                    pricingCategory: null,
                    strikeOffDetails: {
                        displayAmount: pricingData[item],
                        strikeOffAmount: 0
                    }
                }
            }
            if (item === 'discount' && pricingData[item] > 0) {
                pricingElements.push({
                    displayText: t('discounts'),
                    type: 'COUPON',
                    tooltipMessage: null,
                    amount: pricingData[item],
                    pricingCategory: 'DIS',
                    strikeOffDetails: null
                })
            }
        }
    })
    if (paymentDetails && paymentDetails.payment) {
        if (paymentDetails.payment.emiDetails && paymentDetails.payment.emiDetails.emiDiscountAmount > 0) {
            pricingElements.push({
                displayText: 'No Cost EMI discount',
                type: 'NC_EMI',
                tooltipMessage: null,
                amount: -paymentDetails.payment.emiDetails.emiDiscountAmount,
                pricingCategory: 'DIS',
                strikeOffDetails: null
            })
        }
        if (
            paymentDetails.payment.cardlessEMIDetails &&
            paymentDetails.payment.cardlessEMIDetails.cardlessEmiDiscountAmount > 0
        ) {
            pricingElements.push({
                displayText: 'No Cost EMI discount',
                type: 'NC_CARDLESS_EMI',
                tooltipMessage: null,
                amount: -paymentDetails.payment.cardlessEMIDetails.cardlessEmiDiscountAmount,
                pricingCategory: 'DIS',
                strikeOffDetails: null
            })
        }
    }
    newPricingData['pricingElements'] = pricingElements
    newPricingData['finalPricing'] = finalPricing
    return newPricingData
}

export const getTotalFlightDuration = (flightData: Flight) => {
    let totalFlightDuration = flightData?.totalDuration || 0
    if (flightData?.layOverDurations?.length) {
        totalFlightDuration += flightData.layOverDurations.reduce((a, b) => {
            return a + b
        })
    }
    return totalFlightDuration
}
